import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Palette } from 'lucide-react';
import '@fontsource-variable/geist/wght.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import '../../process-config/ConfigStatusView2.css';
import '../../process-config/components/DefeitosVerveTableV2.css';
import {
  ColorSwatchCellDv2,
  DefeitosColorPalettePopover,
  isDefeitoColorConfigured,
} from '../../process-config/components/defeitosVerveTableUi';
import {
  compressGoBrandLogo,
  GO_BRAND_LOGO_ACCEPT,
  GO_BRAND_LOGO_MAX_BYTES,
} from '../utils/compressGoBrandLogo';
import './GoCustomizacaoConfig.css';

const API_BASE_URL = getApiBaseUrl();
const DEFAULT_PRIMARY_COLOR = '#6E56CF';

function Pcs2Section({ icon: Icon, title, desc, children, className = '' }) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2Subblock({ title, desc, children }) {
  return (
    <div className="pcs2-subblock">
      {(title || desc) ? (
        <div className="pcs2-subhead">
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

function normalizeHex(value) {
  const c = String(value ?? '').trim();
  if (!c) return '';
  const withHash = c.startsWith('#') ? c : `#${c}`;
  return withHash.slice(0, 7).toUpperCase();
}

export default function GoCustomizacaoConfig() {
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();
  const logoInputRef = useRef(null);
  const colorBtnRef = useRef(null);
  const colorSaveTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [colorOpen, setColorOpen] = useState(false);
  const [colorSaving, setColorSaving] = useState(false);
  const previewBlobRef = useRef(null);

  const revokePreview = useCallback(() => {
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/go/customization`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Falha ao carregar customização');
      }
      revokePreview();
      setPrimaryColor(normalizeHex(data.data?.primaryColor) || DEFAULT_PRIMARY_COLOR);
      setLogoUrl(data.data?.logoViewUrl || null);
    } catch (e) {
      setError(e.message || 'Erro ao carregar');
      setLogoUrl(null);
    } finally {
      setLoading(false);
    }
  }, [revokePreview]);

  useEffect(() => {
    loadData();
    return () => {
      revokePreview();
      if (colorSaveTimerRef.current) clearTimeout(colorSaveTimerRef.current);
    };
  }, [loadData, revokePreview]);

  const persistPrimaryColor = useCallback(
    async (hex) => {
      const color = normalizeHex(hex);
      if (!isDefeitoColorConfigured(color)) return;
      setColorSaving(true);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/config/go/customization/primary-color`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ primary_color: color }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Falha ao salvar cor');
        }
        setPrimaryColor(data.data?.primaryColor || color);
        toastSuccess('Cor do App atualizada');
      } catch (e) {
        toastError(e.message || 'Erro ao salvar cor');
        await loadData();
      } finally {
        setColorSaving(false);
      }
    },
    [loadData, toastError, toastSuccess]
  );

  const onColorDraftChange = useCallback(
    (hex) => {
      const color = normalizeHex(hex);
      if (!isDefeitoColorConfigured(color)) return;
      setPrimaryColor(color);
      if (colorSaveTimerRef.current) clearTimeout(colorSaveTimerRef.current);
      colorSaveTimerRef.current = setTimeout(() => {
        persistPrimaryColor(color);
      }, 350);
    },
    [persistPrimaryColor]
  );

  const handleLogoSelect = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (!String(file.type || '').startsWith('image/')) {
        toastError('Selecione somente arquivos de imagem.');
        return;
      }
      if (file.size > GO_BRAND_LOGO_MAX_BYTES) {
        toastError('Imagem muito grande. Máximo 20MB.');
        return;
      }

      setLogoUploading(true);
      try {
        const originalSizeBytes = file.size;
        const compressed = await compressGoBrandLogo(file);
        const token = localStorage.getItem('authToken');

        const presignRes = await fetch(`${API_BASE_URL}/api/config/go/customization/logo/presign`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mimeType: compressed.type,
            sizeBytes: compressed.size,
            fileName: compressed.name,
            originalSizeBytes,
          }),
        });
        const presignData = await presignRes.json();
        if (!presignRes.ok || !presignData.success) {
          throw new Error(presignData.message || 'Falha ao preparar upload');
        }

        let uploadRes;
        try {
          uploadRes = await fetch(presignData.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': compressed.type },
            body: compressed,
          });
        } catch {
          throw new Error(
            'Não deu para enviar a logo por este endereço. O armazenamento de imagens bloqueou o envio (rede/CORS). Tente pela rede interna ou peça ao suporte para liberar este acesso.'
          );
        }
        if (!uploadRes.ok) {
          throw new Error(
            'Não deu para enviar a logo agora. Tente de novo em instantes; se continuar, fale com o suporte.'
          );
        }

        const confirmRes = await fetch(`${API_BASE_URL}/api/config/go/customization/logo`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ key: presignData.key }),
        });
        const confirmData = await confirmRes.json();
        if (!confirmRes.ok || !confirmData.success) {
          throw new Error(confirmData.message || 'Falha ao salvar logo');
        }

        revokePreview();
        const localPreview = URL.createObjectURL(compressed);
        previewBlobRef.current = localPreview;
        setLogoUrl(confirmData.data?.logoUrl || localPreview);
        toastSuccess('Logo atualizada');
      } catch (e) {
        const msg = String(e?.message || '');
        if (/failed to fetch|networkerror|load failed|network request failed/i.test(msg)) {
          toastError(
            'Não deu para enviar a logo. Sem conexão com o servidor ou o envio foi bloqueado neste endereço. Confira a rede e tente de novo.'
          );
        } else {
          toastError(msg || 'Não deu para enviar a logo. Tente de novo.');
        }
      } finally {
        setLogoUploading(false);
      }
    },
    [revokePreview, toastError, toastSuccess]
  );

  return (
    <div className="pcs2-page go-customizacao-page">
      <Pcs2Section
        className="go-customizacao-section"
        icon={Palette}
        title="Customização"
        desc="Configure marca e cores visíveis no App Go"
      >
        {loading ? (
          <ProcessConfigSkeleton variant="settings" embedded ariaLabel="Carregando customização" />
        ) : error ? (
          <div className="go-cust-error">
            <span>{error}</span>
            <button type="button" className="go-cust-retry" onClick={loadData}>
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            <Pcs2Subblock
              title="Marca"
              desc="Defina a logo da empresa para ser exibida na tela inicial"
            >
              <div className="go-cust-logo-row">
                <div className="go-cust-logo-preview" aria-hidden={!logoUrl}>
                  {logoUrl ? (
                    <img src={logoUrl} alt="" className="go-cust-logo-preview-img" />
                  ) : (
                    <ImageIcon size={20} strokeWidth={2} aria-hidden />
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept={GO_BRAND_LOGO_ACCEPT}
                  className="go-cust-logo-input-hidden"
                  onChange={handleLogoSelect}
                />
                <button
                  type="button"
                  className="user-form-link-btn go-cust-logo-update-btn"
                  disabled={logoUploading}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoUploading ? 'Enviando...' : 'Atualizar logo'}
                </button>
                <div className="go-cust-logo-hints">
                  <span>Máximo 20MB · JPEG, PNG ou WebP</span>
                </div>
              </div>
            </Pcs2Subblock>

            <hr className="pcs2-divider" />

            <Pcs2Subblock title="Cor do App" desc="Cor predominante padrão no App Go">
              <div className="go-cust-color-row">
                <button
                  ref={colorBtnRef}
                  type="button"
                  className="go-cust-color-trigger"
                  disabled={colorSaving}
                  aria-label="Alterar cor do App"
                  onClick={() => setColorOpen((open) => !open)}
                >
                  <ColorSwatchCellDv2 hex={primaryColor} />
                  <span className="go-cust-color-hex">{primaryColor}</span>
                </button>
              </div>
              <DefeitosColorPalettePopover
                open={colorOpen}
                anchorRef={colorBtnRef}
                draftColor={primaryColor}
                onDraftChange={onColorDraftChange}
                onClose={() => setColorOpen(false)}
              />
            </Pcs2Subblock>
          </>
        )}
      </Pcs2Section>
      {Toaster}
    </div>
  );
}
